"use client"

/**
 * Shared results UI used by both the modal and the /search page.
 *
 * Stateless w.r.t. fetching — the parent decides whether the user has
 * typed anything and passes either `groups` (results) or a falsy value
 * (initial / empty state). Renders:
 *
 *   - Initial state: popular pages (editorial eyebrows) + prompt seeds.
 *   - Loading state: skeletons.
 *   - Results: type-grouped list. Section headers carry the type label;
 *     result rows show ONLY a sub-category chip when one exists, so we
 *     don't get "PAGES … PAGES … PAGES" repeating between header + row.
 *   - No results: prominent "Talk to our team" CTA.
 *
 * Keyboard navigation lives here because both surfaces want it. The
 * caller exposes a `selectedIndex` + `setSelectedIndex` to keep the
 * arrow-key controller in sync with the input that owns focus.
 */

import { entryMriTierKey } from "@/data/mri-packages"
import Link from "next/link"
import { useTranslations } from "@/lib/intl/use-translations"
import { Fragment, useEffect, useMemo, useState } from "react"
import { cn } from "@/lib/utils"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { popularPagesForCountry, PROMPT_SEEDS } from "@/lib/search/presets"
import { highlightExcerpt } from "@/lib/search/engine"
import { track } from "@/lib/analytics"
import { useCountry } from "@/components/layout/country-provider"
import { isAllowedInCountry, PATH_TO_PAGE_GATE } from "@/lib/country-gates"
import type { Country } from "@/i18n/config"
import { fetchPrices, fallbackFullPrice } from "@/lib/pricing"
import { ArrowLeftIcon } from "@/components/ui/icons"
import type { ResultGroup } from "@/lib/search/client"
import type { SearchHit } from "@/lib/search/engine"

/**
 * Escape hatch for dynamic translation keys — `next-intl` narrows `t(key)`
 * to literal keys declared in the `Messages` augmentation, so a key
 * assembled at runtime (`popular.${id}.title`) errors at compile time even
 * though it resolves correctly. Mirrors `dyn()` in `lib/native-markdown/shared.ts`.
 */
function dynT(t: ReturnType<typeof useTranslations>, key: string): string {
  return (t as unknown as (k: string) => string)(key)
}

function PopularFromPrice({
  mapKey,
  className,
}: {
  mapKey: "core" | "blood"
  className?: string
}) {
  const { code: country } = useCountry()
  const t = useTranslations("search")
  // "core" stands for the full-body MRI's "from" price: the market's entry
  // tier, which is Advanced in Austria (no Core there).
  const priceKey = mapKey === "core" ? entryMriTierKey(country) : mapKey
  const initial = useMemo(
    () => fallbackFullPrice(country, priceKey),
    [country, priceKey]
  )
  const [fullPrice, setFullPrice] = useState(initial)

  useEffect(() => {
    setFullPrice(fallbackFullPrice(country, priceKey))
    let cancelled = false
    fetchPrices(country).then((data) => {
      const fp = data.fullPrices[priceKey]
      if (!cancelled && fp) setFullPrice(fp)
    })
    return () => {
      cancelled = true
    }
  }, [country, priceKey])

  return (
    <span className={cn("type-body-md text-neutral-500", className)}>
      {t("popularPriceFrom", { price: fullPrice })}
    </span>
  )
}

interface ResultsViewProps {
  query: string
  groups: ResultGroup[] | null
  loading: boolean
  /** Flat ranked list of hits — used for keyboard nav across groups. */
  flatHits: SearchHit[]
  selectedIndex: number
  onHover: (index: number) => void
  onResultClick: (hit: SearchHit, rank: number) => void
  /** Receives the literal pill label as `query` — the visible text IS the search. */
  onPromptClick: (query: string, promptId: string) => void
  surface: "modal" | "page"
  country: Country
  locale: string
  /**
   * Pages glue's semantic index matched when the lexical engine found nothing,
   * already resolved against this site's own search index — so these are
   * ordinary `SearchHit`s and render through the same `ResultRow` as any other
   * result. See `use-kb-fallback.ts` for why the KB only ever supplies a URL.
   */
  kbFallback?: { hits: SearchHit[]; status: "idle" | "loading" | "done" }
}

export function SearchResultsView({
  query,
  groups,
  loading,
  flatHits,
  selectedIndex,
  onHover,
  onResultClick,
  onPromptClick,
  surface,
  country,
  locale,
  kbFallback,
}: ResultsViewProps) {
  const t = useTranslations("search")
  const nt = useTranslations("nav")
  const localePath = useLocalePath()

  if (!query.trim()) {
    return (
      <div className="flex flex-col gap-6 pt-4 pb-5">
        {/* Prompt pills sit right below the input — clean white chips
         *  on the neutral-50 surface, no border / no arrow. Their
         *  proximity to the search field is what reads as "try one of
         *  these"; the label is implicit. Horizontal scroll on mobile
         *  so the row stays a single line. */}
        <ul className="flex flex-nowrap gap-2 overflow-x-auto px-4 [scrollbar-width:none] md:flex-wrap md:px-5 [&::-webkit-scrollbar]:hidden">
          {PROMPT_SEEDS.map((entry) => {
            const label = dynT(t, `prompt.${entry.id}`)
            return (
              <li key={entry.id} className="shrink-0">
                <button
                  type="button"
                  onClick={() => onPromptClick(label, entry.id)}
                  className="type-body-md cursor-pointer rounded-full bg-white px-4 py-2 text-neutral-900 transition-colors hover:bg-neutral-100"
                >
                  {label}
                </button>
              </li>
            )
          })}
        </ul>
        <div className="px-4 md:px-5">
          <Section title={t("popularLabel")}>
            <ul className="grid grid-cols-1 gap-0.5 sm:grid-cols-2">
              {/* Skip destinations gated out of this country (membership on
                  waitlist NL, insurance outside CH) — a popular link must
                  never land on a 404. */}
              {popularPagesForCountry(country)
                .filter((entry) => {
                  const gate =
                    PATH_TO_PAGE_GATE[
                      entry.path as keyof typeof PATH_TO_PAGE_GATE
                    ]
                  return !gate || isAllowedInCountry(gate, country)
                })
                .map((entry, index) => (
                  <li key={entry.id}>
                    <Link
                      href={localePath(entry.path)}
                      className="flex flex-col rounded-xl px-4 py-2.5 transition-colors hover:bg-white"
                      onClick={() =>
                        track.searchPopularClicked({
                          popular_id: entry.id,
                          rank: index,
                          surface,
                          country,
                          locale,
                        })
                      }
                    >
                      <span className="type-title-md flex items-center gap-2 text-neutral-900">
                        {dynT(t, `popular.${entry.id}.title`)}
                        {entry.isNew && (
                          <span className="type-label-sm bg-bright-blue-700 rounded-full px-1.5 py-0.5 font-medium tracking-wider whitespace-nowrap text-white">
                            {nt("newBadge")}
                          </span>
                        )}
                      </span>
                      {entry.priceFromKey ? (
                        <PopularFromPrice mapKey={entry.priceFromKey} />
                      ) : (
                        <span className="type-body-md text-neutral-500">
                          {dynT(t, `popular.${entry.id}.subtitle`)}
                        </span>
                      )}
                    </Link>
                  </li>
                ))}
            </ul>
          </Section>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="px-4 py-5 md:px-5">
        <ul className="flex flex-col gap-2" aria-label={t("loadingLabel")}>
          {[0, 1, 2, 3].map((i) => (
            <li
              key={i}
              className="h-14 animate-pulse rounded-lg bg-neutral-100"
            />
          ))}
        </ul>
      </div>
    )
  }

  if (!groups || groups.length === 0) {
    const kbHits = kbFallback?.hits ?? []
    const kbLoading = kbFallback?.status === "loading"
    return (
      <div className="flex flex-col items-start gap-4 px-4 py-8 md:px-5">
        {kbLoading ? (
          // Pending KB fallback: a quiet skeleton instead of a premature
          // "no results" claim while the lookup is still in flight (the
          // gate + settle window can run ~600ms-2s before it resolves).
          <ul
            className="flex w-full flex-col gap-2"
            aria-label={t("loadingLabel")}
          >
            {[0, 1].map((i) => (
              <li
                key={i}
                className="h-14 animate-pulse rounded-lg bg-neutral-100"
              />
            ))}
          </ul>
        ) : kbHits.length > 0 ? (
          // Same `ResultRow` as a lexical hit, deliberately: these ARE pages on
          // this site, found by a better matcher. The only thing that marks
          // them out is the label — the rows should not look like a different
          // kind of thing, because they are not one.
          <div className="flex w-full flex-col gap-2">
            <h3 className="type-label-md text-neutral-500">
              {t("kbFallbackLabel")}
            </h3>
            <ul className="flex flex-col gap-0.5">
              {kbHits.map((hit, rank) => (
                <ResultRow
                  key={hit.doc.id}
                  hit={hit}
                  query={query}
                  selected={false}
                  onClick={() =>
                    track.searchKbFallbackClicked({
                      query,
                      surface,
                      url: hit.doc.url,
                      rank,
                      locale,
                    })
                  }
                  onMouseEnter={() => undefined}
                />
              ))}
            </ul>
          </div>
        ) : (
          <p className="type-body-lg text-neutral-700">
            {t("noResults", { query })}
          </p>
        )}
        <Link
          href={localePath("/contact")}
          className="bg-aubergine-800 hover:bg-aubergine-900 type-body-md inline-flex items-center gap-2 rounded-full px-4 py-2 text-white transition-colors"
        >
          {t("noResultsCta")}
          <ArrowLeftIcon
            width={14}
            height={14}
            className="rotate-180"
            aria-hidden="true"
          />
        </Link>
      </div>
    )
  }

  let runningIndex = 0
  return (
    <div className="flex flex-col">
      {groups.map((group) => {
        const items = group.hits.map((hit) => {
          const idx = runningIndex
          runningIndex++
          return (
            <ResultRow
              key={hit.doc.id}
              hit={hit}
              query={query}
              selected={idx === selectedIndex}
              onClick={() => onResultClick(hit, flatHits.indexOf(hit))}
              onMouseEnter={() => onHover(idx)}
            />
          )
        })
        return (
          <section
            key={group.type}
            className="border-b border-neutral-100 last:border-b-0"
          >
            <h3 className="type-label-md px-4 pt-5 pb-2 text-neutral-500 md:px-5">
              {dynT(t, `type.${group.type}`)}
            </h3>
            <ul className="flex flex-col gap-0.5 px-2 pb-4 md:px-3">{items}</ul>
          </section>
        )
      })}
    </div>
  )
}

function Section({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="type-label-md px-4 text-neutral-500">{title}</h3>
      {children}
    </section>
  )
}

function ResultRow({
  hit,
  query,
  selected,
  onClick,
  onMouseEnter,
}: {
  hit: SearchHit
  query: string
  selected: boolean
  onClick: () => void
  onMouseEnter: () => void
}) {
  const segments = highlightExcerpt(hit.doc.excerpt ?? "", query)
  return (
    <li>
      <Link
        href={hit.doc.url}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        className={cn(
          "block rounded-xl px-4 py-2.5 transition-colors",
          selected ? "bg-white" : "hover:bg-white"
        )}
      >
        {/* Per-row eyebrow: ONLY the sub-category, never re-stating the
            section type. The section header above ("Biomarkers") already
            carries that information; "BIOMARKERS · HEART" → "Heart" is the
            real signal here. */}
        {hit.doc.category ? (
          <span className="type-label-md mb-0.5 block text-neutral-500">
            {hit.doc.category}
          </span>
        ) : null}
        <span className="type-title-md block truncate text-neutral-900">
          <HighlightedRuns segments={highlightExcerpt(hit.doc.title, query)} />
        </span>
        {hit.doc.excerpt ? (
          <span className="type-body-md mt-0.5 line-clamp-2 block text-neutral-500">
            <HighlightedRuns segments={segments} />
          </span>
        ) : null}
      </Link>
    </li>
  )
}

/**
 * Render highlight segments inline. Caller wraps the runs in whatever
 * typography they want (`<span>`, `<p>`, etc.) — this just emits the
 * `<mark>` / text fragments. Mark uses a semibold neutral treatment with
 * no fill so it reads as editorial emphasis, not the Linear/Vercel-template
 * yellow highlight.
 */
function HighlightedRuns({
  segments,
}: {
  segments: ReturnType<typeof highlightExcerpt>
}) {
  return (
    <>
      {segments.map((seg, i) =>
        seg.match ? (
          <mark
            key={i}
            className="bg-transparent font-semibold text-neutral-900"
          >
            {seg.text}
          </mark>
        ) : (
          <Fragment key={i}>{seg.text}</Fragment>
        )
      )}
    </>
  )
}
